import { ReactElement, useCallback, useState } from 'react';
import { useRequestServiceModalContext } from '@/creditflowresearch/application/RequestServiceModalContext';
import { checkAccess } from '@/creditflowresearch/application/User/access';
import Button from '@creditflowresearch/uilib/Button';
import { ServiceType } from '@/creditflowresearch/domain/ServiceType';
import { RestrictedAccessExplanation } from '@/creditflowresearch/ui/common/components/Explanations';
import RequestServiceModal from '@/creditflowresearch/ui/common/components/RequestServiceModal';
import type { PopoverPlacement } from '@creditflowresearch/uilib/Popover';

import styles from './withExportRestriction.module.scss';

const DEFAULT_DESCRIPTION = 'Exports are offered at an additional cost. ' +
                            'Please contact us for further pricing details and access.';

interface IChildrenProps {
  isRestricted: boolean,
  toggleExplanation: () => void,
}

interface IWithExportRestrictionProps {
  children: (props: IChildrenProps) => ReactElement,
  conditions: boolean[],
  serviceType?: ServiceType,
  title?: string,
  description?: string,
  placement?: PopoverPlacement,
}

const WithExportRestriction = ({
  children,
  conditions,
  serviceType = ServiceType.BONDSCREENER_EXPORT,
  title = 'Market Data Export',
  description = DEFAULT_DESCRIPTION,
  placement = 'bottom-end',
}: IWithExportRestrictionProps) => {
  const [isExplanationOpen, setIsExplanationOpen] = useState(false);
  const { openModal } = useRequestServiceModalContext();

  const toggleExplanation = () => setIsExplanationOpen((prev) => !prev);

  const contactButtonHandle = useCallback(() => {
    openModal(serviceType);
    toggleExplanation();
  }, []);

  if (checkAccess(conditions)) {
    return children({ isRestricted: false, toggleExplanation: () => {} });
  }

  const renderContactButton = () => (
    <Button
      variant="solid"
      dataTest="bondscreenerExplanationContactButton"
      className={ styles.explanationContactButton }
      onClick={ contactButtonHandle }
    >
      Contact Us
    </Button>
  );

  return (
    <>
      <RestrictedAccessExplanation
        title={ title }
        description={ description }
        isOpen={ isExplanationOpen }
        closeTooltip={ () => setIsExplanationOpen(false) }
        referenceClassName={ styles.explanationReference }
        popoverContentClass={ styles.popoverContentClass }
        customButton={ renderContactButton }
        placement={ placement }
        usePortal={ false }
      >
        { children({ isRestricted: true, toggleExplanation }) }
      </RestrictedAccessExplanation>
      <RequestServiceModal />
    </>
  );
};

export default WithExportRestriction;
